import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { Patient, PatientStatus, PatientPriority, PaymentMode } from '@/types';
import { supabase } from '@/lib/supabase';
import { generatePatientId as generateYearSeqPatientId } from '@/lib/utils/patientId';
import { trackChange } from '@/lib/utils/sync-tracker';

interface PatientsState {
  patients: Patient[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
  pendingUpdates: Set<string>; // Track optimistic updates
  fetchPatients: () => Promise<void>;
  addPatient: (patient: {
    name: string;
    age: string;
    gender: 'male' | 'female' | 'other';
    contactNumber: string;
    priority?: PatientPriority;
    reason: string;
    referralDoctor?: string;
    location?: string;
    doctorId?: string;
    registeredAt?: string;
  }) => Promise<void>;
  updatePatientStatus: (patientId: string, status: PatientStatus, completedBy?: string) => Promise<void>;
  updatePatientDoctor: (patientId: string, doctorId: string) => Promise<void>;
  addPatientServices: (patientId: string, services: any[]) => Promise<void>;
  removePatientService: (patientId: string, serviceId: string) => Promise<void>;
  updatePatientPayment: (patientId: string, amount: number, paymentMode: PaymentMode) => Promise<void>;
  updatePatientFollowUp: (patientId: string, followUpDate: Date, notes?: string) => Promise<void>;
  resetPatientSession: (patientId: string) => Promise<void>;
  removePatient: (id: string) => Promise<void>;
  getPatientsByStatus: (status: PatientStatus) => Patient[];
  getPatientsByDoctor: (doctorId: string) => Patient[];
  updatePatient: (patientId: string, updates: Partial<Patient>) => Promise<void>;
}

function loadPatientsCache() {
  try {
    const cache = localStorage.getItem('patients-cache');
    if (cache) return JSON.parse(cache);
  } catch {}
  return [];
}
function savePatientsCache(patients: Patient[]) {
  try {
    localStorage.setItem('patients-cache', JSON.stringify(patients));
  } catch {}
}

export const usePatientsStore = create<PatientsState>()((set, get) => ({
  patients: loadPatientsCache(),
  isLoading: false,
  error: null,
  lastUpdated: 0,
  pendingUpdates: new Set(),

  fetchPatients: async () => {
    set({ isLoading: true, error: null });
    try {
      // Fetching patients from database
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('registered_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching patients:', error);
        throw error;
      }

      // Processing patient data

      // Map snake_case to camelCase
      const mappedPatients = (data || []).map(patient => ({
        id: patient.id,
        name: patient.name,
        age: patient.age,
        gender: patient.gender,
        contactNumber: patient.contact_number,
        priority: patient.priority,
        reason: patient.reason,
        referralDoctor: patient.referral_doctor,
        location: patient.location,
        doctorId: patient.doctor_id,
        registeredAt: patient.registered_at,
        status: patient.status,
        amountPaid: patient.amount_paid,
        paymentMode: patient.payment_mode,
        paymentHistory: patient.payment_history,
        services: patient.services,
        followUpDate: patient.follow_up_date,
        followUpStatus: patient.follow_up_status || 'scheduled',
        notes: patient.notes,
        createdAt: patient.created_at,
        updatedAt: patient.updated_at,
        appointmentTime: patient.appointment_time,
        completedAt: patient.completed_at,
        completedBy: patient.completed_by
      }));

      // Patient data mapping completed
      set({ patients: mappedPatients, isLoading: false, lastUpdated: Date.now() });
      savePatientsCache(mappedPatients);
    } catch (error) {
      console.error('Error fetching patients:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  addPatient: async (patient) => {
    try {
      console.log('Adding new patient with optimistic update:', patient);
      const now = patient.registeredAt || new Date().toISOString();
      const currentPatients = get().patients;
      
      // Create optimistic patient object
      const optimisticPatient: Patient = {
        id: generateYearSeqPatientId(currentPatients),
        name: patient.name,
        age: patient.age,
        gender: patient.gender,
        contactNumber: patient.contactNumber,
        priority: patient.priority || 'normal',
        reason: patient.reason,
        referralDoctor: patient.referralDoctor,
        location: patient.location,
        doctorId: patient.doctorId,
        registeredAt: now,
        status: 'waiting' as PatientStatus,
        amountPaid: 0,
        paymentMode: 'cash' as PaymentMode,
        paymentHistory: [],
        services: [],
        followUpDate: null,
        notes: '',
        createdAt: now,
        updatedAt: now,
        appointmentTime: undefined,
        completedAt: undefined,
        completedBy: undefined
      };

      // Optimistic update - immediately add to UI
      set(state => {
        const updated = [optimisticPatient, ...state.patients];
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, optimisticPatient.id])
        };
      });

      // Note: Patient is created directly in database below, no need to track for sync

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .insert([{
          id: optimisticPatient.id,
          name: patient.name,
          age: patient.age,
          gender: patient.gender,
          contact_number: patient.contactNumber,
          priority: patient.priority || 'normal',
          reason: patient.reason,
          referral_doctor: patient.referralDoctor,
          location: patient.location,
          doctor_id: patient.doctorId,
          registered_at: now, // always snake_case for backend
          status: 'waiting',
          amount_paid: 0,
          payment_mode: 'cash',
          payment_history: [],
          services: [],
          follow_up_date: null,
          notes: '',
          created_at: now,
          updated_at: now
        }])
        .select();

      if (error) {
        console.error('Supabase error:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.filter(p => p.id !== optimisticPatient.id);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== optimisticPatient.id)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data[0].id,
        name: data[0].name,
        age: data[0].age,
        gender: data[0].gender,
        contactNumber: data[0].contact_number,
        priority: data[0].priority,
        reason: data[0].reason,
        referralDoctor: data[0].referral_doctor,
        location: data[0].location,
        doctorId: data[0].doctor_id,
        registeredAt: data[0].registered_at,
        status: data[0].status,
        amountPaid: data[0].amount_paid,
        paymentMode: data[0].payment_mode,
        paymentHistory: data[0].payment_history,
        services: data[0].services,
        followUpDate: data[0].follow_up_date,
        followUpStatus: data[0].follow_up_status || 'scheduled',
        notes: data[0].notes,
        appointmentTime: data[0].appointment_time,
        createdAt: data[0].created_at,
        updatedAt: data[0].updated_at,
        completedAt: data[0].completed_at,
        completedBy: data[0].completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === optimisticPatient.id ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== optimisticPatient.id))
        };
      });
    } catch (error) {
      console.error('Error adding patient:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatient: async (patientId, updates) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Prepare backend update object
      const backendUpdates: any = { ...updates };
      if ('registeredAt' in backendUpdates) {
        backendUpdates['registered_at'] = backendUpdates['registeredAt'];
        delete backendUpdates['registeredAt'];
      }
      if ('followUpStatus' in backendUpdates) {
        backendUpdates['follow_up_status'] = backendUpdates['followUpStatus'];
        delete backendUpdates['followUpStatus'];
      }
      if ('followUpDate' in backendUpdates) {
        backendUpdates['follow_up_date'] = backendUpdates['followUpDate'];
        delete backendUpdates['followUpDate'];
      }
      if ('doctorId' in backendUpdates) {
        backendUpdates['doctor_id'] = backendUpdates['doctorId'];
        delete backendUpdates['doctorId'];
      }
      if ('contactNumber' in backendUpdates) {
        backendUpdates['contact_number'] = backendUpdates['contactNumber'];
        delete backendUpdates['contactNumber'];
      }
      if ('referralDoctor' in backendUpdates) {
        backendUpdates['referral_doctor'] = backendUpdates['referralDoctor'];
        delete backendUpdates['referralDoctor'];
      }
      if ('amountPaid' in backendUpdates) {
        backendUpdates['amount_paid'] = backendUpdates['amountPaid'];
        delete backendUpdates['amountPaid'];
      }
      if ('paymentMode' in backendUpdates) {
        backendUpdates['payment_mode'] = backendUpdates['paymentMode'];
        delete backendUpdates['paymentMode'];
      }
      if ('paymentHistory' in backendUpdates) {
        backendUpdates['payment_history'] = backendUpdates['paymentHistory'];
        delete backendUpdates['paymentHistory'];
      }
      if ('appointmentTime' in backendUpdates) {
        backendUpdates['appointment_time'] = backendUpdates['appointmentTime'];
        delete backendUpdates['appointmentTime'];
      }
      if ('completedAt' in backendUpdates) {
        backendUpdates['completed_at'] = backendUpdates['completedAt'];
        delete backendUpdates['completedAt'];
      }
      if ('completedBy' in backendUpdates) {
        backendUpdates['completed_by'] = backendUpdates['completedBy'];
        delete backendUpdates['completedBy'];
      }

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          ...backendUpdates,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          ...currentPatient
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          ...backendUpdates,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        followUpStatus: data.follow_up_status || 'scheduled',
        notes: data.notes,
        appointmentTime: data.appointment_time,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientStatus: async (patientId, status, completedBy) => {
    try {
      const now = new Date().toISOString();
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        status,
        ...(status === 'completed' && { completedAt: now }),
        ...(completedBy && { completedBy }),
        updatedAt: now
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          status,
          ...(status === 'completed' && { completed_at: now }),
          ...(completedBy && { completed_by: completedBy }),
          updated_at: now
        },
        originalData: {
          status: currentPatient.status,
          completed_at: currentPatient.completedAt,
          completed_by: currentPatient.completedBy,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          status,
          ...(status === 'completed' && { completed_at: now }),
          ...(completedBy && { completed_by: completedBy }),
          updated_at: now
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient status:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        appointmentTime: data.appointment_time,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient status:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientDoctor: async (patientId, doctorId) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        doctorId,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          doctor_id: doctorId,
          updated_at: new Date().toISOString()
        },
        originalData: {
          doctor_id: currentPatient.doctorId,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({ doctor_id: doctorId })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient doctor:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient doctor:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientPayment: async (patientId, amount, paymentMode) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      const now = new Date().toISOString();
      const newPaymentHistory = [
        ...(currentPatient.paymentHistory || []),
        {
          amount,
          mode: paymentMode,
          date: now
        }
      ];

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        amountPaid: (currentPatient.amountPaid || 0) + amount,
        paymentMode,
        paymentHistory: newPaymentHistory,
        updatedAt: now
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          amount_paid: optimisticPatient.amountPaid,
          payment_mode: paymentMode,
          payment_history: newPaymentHistory,
          updated_at: now
        },
        originalData: {
          amount_paid: currentPatient.amountPaid,
          payment_mode: currentPatient.paymentMode,
          payment_history: currentPatient.paymentHistory,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          amount_paid: optimisticPatient.amountPaid,
          payment_mode: paymentMode,
          payment_history: newPaymentHistory,
          updated_at: now
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating patient payment:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error updating patient payment:', error);
      set({ error: (error as Error).message });
    }
  },

  addPatientServices: async (patientId, services) => {
    set({ isLoading: true, error: null });
    try {
      const patient = get().patients.find(p => p.id === patientId);
      if (!patient) throw new Error('Patient not found');

      // Format the services to match PatientService interface
      const formattedServices = services.map(service => ({
        id: uuidv4(),
        service_id: service.id,
        patient_id: patientId,
        service_name: service.name,
        price: service.price,
        added_at: new Date().toISOString()
      }));

      // Create optimistic update - immediately update UI
      const optimisticPatient = {
        ...patient,
        services: [...(patient.services || []), ...formattedServices],
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          services: patient.services,
          updated_at: patient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error adding patient services:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? patient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        appointmentTime: data.appointment_time,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
      throw error;
    }
  },

  removePatientService: async (patientId, serviceId) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      const serviceToRemove = currentPatient.services.find(s => s.id === serviceId);
      if (!serviceToRemove) throw new Error('Service not found');

      // Create optimistic update
      const optimisticPatient = {
        ...currentPatient,
        services: currentPatient.services.filter(s => s.id !== serviceId),
        updatedAt: new Date().toISOString()
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        },
        originalData: {
          services: currentPatient.services,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync (now handled by incremental sync)
      const { data, error } = await supabase
        .from('patients')
        .update({ 
          services: optimisticPatient.services,
          updated_at: optimisticPatient.updatedAt
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error removing patient service:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });

      console.log(`Service ${serviceToRemove.service_name} removed from patient ${currentPatient.name}`);
    } catch (error) {
      console.error('Error removing patient service:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  resetPatientSession: async (patientId) => {
    try {
      const currentPatient = get().patients.find(p => p.id === patientId);
      if (!currentPatient) throw new Error('Patient not found');

      const now = new Date().toISOString();
      
      // Create optimistic update - reset payment and services for new session
      // BUT PRESERVE payment history for future visits
      const optimisticPatient: Patient = {
        ...currentPatient,
        amountPaid: 0,
        paymentMode: 'cash' as PaymentMode,
        paymentHistory: currentPatient.paymentHistory || [], // PRESERVE payment history
        services: [],
        updatedAt: now
      };

      // Optimistic update - immediately update UI
      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? optimisticPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, patientId]),
          lastUpdated: Date.now() // Force re-render
        };
      });

      // Track change for incremental sync
      trackChange({
        id: patientId,
        table: 'patients',
        operation: 'update',
        data: {
          amount_paid: 0,
          payment_mode: 'cash',
          payment_history: currentPatient.paymentHistory || [], // PRESERVE payment history
          services: [],
          updated_at: now
        },
        originalData: {
          amount_paid: currentPatient.amountPaid,
          payment_mode: currentPatient.paymentMode,
          payment_history: currentPatient.paymentHistory,
          services: currentPatient.services,
          updated_at: currentPatient.updatedAt
        }
      });

      // Backend sync
      const { data, error } = await supabase
        .from('patients')
        .update({
          amount_paid: 0,
          payment_mode: 'cash',
          payment_history: currentPatient.paymentHistory || [], // PRESERVE payment history
          services: [],
          updated_at: now
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) {
        console.error('Error resetting patient session:', error);
        // Rollback optimistic update on error
        set(state => {
          const updated = state.patients.map(p => p.id === patientId ? currentPatient : p);
          savePatientsCache(updated);
          return { 
            patients: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data and remove from pending
      const mappedPatient = {
        id: data.id,
        name: data.name,
        age: data.age,
        gender: data.gender,
        contactNumber: data.contact_number,
        priority: data.priority,
        reason: data.reason,
        referralDoctor: data.referral_doctor,
        location: data.location,
        doctorId: data.doctor_id,
        registeredAt: data.registered_at,
        status: data.status,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        paymentHistory: data.payment_history,
        services: data.services,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by
      };

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? mappedPatient : p);
        savePatientsCache(updated);
        return { 
          patients: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== patientId))
        };
      });
    } catch (error) {
      console.error('Error resetting patient session:', error);
      set({ error: (error as Error).message });
    }
  },

  updatePatientFollowUp: async (patientId, followUpDate, notes) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('patients')
        .update({
          follow_up_date: followUpDate.toISOString(),
          notes: notes || ''
        })
        .eq('id', patientId)
        .select()
        .single();

      if (error) throw error;

      set(state => {
        const updated = state.patients.map(p => p.id === patientId ? data : p);
        savePatientsCache(updated);
        return { patients: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  removePatient: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('patients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => {
        const updated = state.patients.filter(p => p.id !== id);
        savePatientsCache(updated);
        return { patients: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  getPatientsByStatus: (status) => {
    return get().patients.filter(p => p.status === status);
  },

  getPatientsByDoctor: (doctorId: string) => {
    return get().patients.filter(p => p.doctorId === doctorId);
  }
}));
